import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Badge, Button, Select } from '@/components/ui/primitives';
import type { ReportFilterProps } from './LabReportsPage';

const PAGE = 50;

interface Range {
  from: string;
  to: string;
}

interface CriticalHistoryRow {
  id: string;
  orderNumber: string;
  patientName: string;
  mrn: string;
  test: string;
  value: string | null;
  unit: string | null;
  flag: string;
  raisedAt: string;
  superseded: boolean;
  referringDoctorName: string | null;
  notifiedAt: string | null;
  delivered: boolean;
  notified: { to: string; status: string; at: string; kind: string }[];
  escalatedAt: string | null;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  toldName: string | null;
  readBackNote: string | null;
  minutesToAcknowledge: number | null;
}

interface UnbilledRow {
  orderId?: string;
  studyId?: string;
  orderNumber?: string;
  studyNumber?: string;
  orderedAt: string;
  patientName: string;
  mrn: string;
  tests?: number;
  study?: string;
  exemption: string | null;
  reason: string | null;
  marked: boolean;
  orderedByName: string | null;
}

interface Page<T> {
  data: T[];
  nextCursor: string | null;
}

function minutesText(value: number): string {
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  if (hours < 24) return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${days} d ${rest} h` : `${days} d`;
}

function loadMoreFooter({ hasNext, loading, onMore }: { hasNext: boolean; loading: boolean; onMore: () => void }) {
  if (!hasNext) return undefined;
  return (
    <div className="flex w-full justify-center">
      <Button variant="outline" size="sm" loading={loading} onClick={onMore}>
        {tr('lab', 'load_more')}
      </Button>
    </div>
  );
}

export function CriticalHistoryTab({
  range,
  showRadiology,
  rangeFields,
  problem,
}: { range: Range; showRadiology: boolean } & ReportFilterProps) {
  const [kind, setKind] = useState<'lab' | 'radiology'>('lab');
  const query = useInfiniteQuery({
    queryKey: ['critical-history', kind, range.from, range.to],
    initialPageParam: '',
    queryFn: ({ pageParam }) => get<Page<CriticalHistoryRow>>(`/lab/reports/criticals${qs({ from: range.from, to: range.to, kind, limit: PAGE, cursor: pageParam })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: problem === null,
  });
  const rows = query.data?.pages.flatMap((page) => page.data);

  return (
    <>
      <SearchFilterBar
        filters={
          <>
            {rangeFields}
            {showRadiology && (
              <FilterField label={tr('lab', 'kind')}>
                <Select className="w-48" value={kind} onChange={(e) => setKind(e.target.value === 'radiology' ? 'radiology' : 'lab')} aria-label={tr('lab', 'kind')}>
                  <option value="lab">{tr('lab', 'laboratory')}</option>
                  <option value="radiology">{tr('lab', 'radiology')}</option>
                </Select>
              </FilterField>
            )}
          </>
        }
        onReset={kind === 'radiology' ? () => setKind('lab') : undefined}
      />
      {problem ? (
        <Alert variant="warning">{problem}</Alert>
      ) : (
        <DataTable
          rows={rows}
          loading={query.isLoading} refreshing={query.isFetching}
          error={query.error}
          rowKey={(row) => row.id}
          empty={tr('lab', 'no_critical_value_was_raised_in_this_period')}
          footer={loadMoreFooter({ hasNext: Boolean(query.hasNextPage), loading: query.isFetchingNextPage, onMore: () => void query.fetchNextPage() })}
          columns={[
            { header: tr('lab', 'raised'), sort: (row) => row.raisedAt, cell: (row) => dateTime(row.raisedAt) },
            {
              header: tr('lab', 'patient'),
              sort: (row) => row.patientName,
              cell: (row) => (
                <div>
                  <p className="font-medium">{row.patientName}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.mrn} · {row.orderNumber}
                  </p>
                </div>
              ),
            },
            {
              header: tr('lab', 'critical'),
              sort: (row) => row.test,
              cell: (row) => (
                <div>
                  <p>{row.test}</p>
                  <p className="text-sm font-semibold">
                    {row.value ?? '—'} {row.unit ?? ''}
                    {row.superseded && <Badge variant="muted" className="ms-1">{tr('lab', 'replaced')}</Badge>}
                  </p>
                </div>
              ),
            },
            {
              header: tr('lab', 'told'),
              cell: (row) =>
                row.delivered ? (
                  <div className="text-xs">
                    {row.notified.length === 0 ? <span className="text-muted-foreground">{tr('lab', 'recorded_no_message_log')}</span> : row.notified.map((message) => (
                      <p key={`${message.to}-${message.at}`}>
                        {message.to} <span className="text-muted-foreground">({humanize(message.status)})</span>
                      </p>
                    ))}
                  </div>
                ) : (
                  <Badge variant="destructive">{tr('lab', 'not_delivered')}</Badge>
                ),
            },
            {
              header: tr('lab', 'acknowledged'),
              sort: (row) => row.acknowledgedAt,
              cell: (row) =>
                row.acknowledgedAt ? (
                  <div className="text-xs">
                    <p>
                      {row.toldName} <span className="text-muted-foreground">{tr('lab', 'by')} {row.acknowledgedBy ?? tr('lab', 'staff')}</span>
                    </p>
                    <p className="text-muted-foreground">{dateTime(row.acknowledgedAt)}</p>
                  </div>
                ) : (
                  <Badge variant="warning">{tr('lab', 'open')}</Badge>
                ),
            },
            {
              header: tr('lab', 'time_to_acknowledge'),
              align: 'right',
              sort: (row) => row.minutesToAcknowledge,
              cell: (row) => (row.minutesToAcknowledge === null ? '—' : minutesText(row.minutesToAcknowledge)),
            },
          ]}
        />
      )}
    </>
  );
}

export function UnbilledTab({ range, radiology }: { range: Range; radiology: boolean }) {
  const query = useInfiniteQuery({
    queryKey: ['unbilled-orders', radiology, range.from, range.to],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<Page<UnbilledRow>>(`/${radiology ? 'radiology' : 'lab'}/reports/unbilled${qs({ from: range.from, to: range.to, limit: PAGE, cursor: pageParam })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = query.data?.pages.flatMap((page) => page.data);

  return (
    <DataTable
      rows={rows}
      loading={query.isLoading} refreshing={query.isFetching}
      error={query.error}
      rowKey={(row) => row.orderId ?? row.studyId ?? row.orderedAt}
      empty={tr('lab', 'no_order_without_a_bill_in_this_period')}
      footer={loadMoreFooter({ hasNext: Boolean(query.hasNextPage), loading: query.isFetchingNextPage, onMore: () => void query.fetchNextPage() })}
      columns={[
        { header: tr('lab', 'ordered'), sort: (row) => row.orderedAt, cell: (row) => dateTime(row.orderedAt) },
        {
          header: radiology ? tr('lab', 'study') : tr('lab', 'order'),
          sort: (row) => row.orderNumber ?? row.studyNumber,
          cell: (row) => <span className="font-mono text-xs">{row.orderNumber ?? row.studyNumber}</span>,
        },
        {
          header: tr('lab', 'patient'),
          sort: (row) => row.patientName,
          cell: (row) => (
            <div>
              <p className="font-medium">{row.patientName}</p>
              <p className="text-xs text-muted-foreground">{row.mrn}</p>
            </div>
          ),
        },
        {
          header: radiology ? tr('lab', 'imaging') : tr('lab', 'tests'),
          sort: (row) => (radiology ? row.study : row.tests),
          cell: (row) => (radiology ? (row.study ?? '') : row.tests),
        },
        {
          header: tr('lab', 'covered_as'),
          sort: (row) => (row.marked ? row.exemption : null),
          cell: (row) => (row.marked ? <Badge variant="secondary">{humanize(row.exemption)}</Badge> : <Badge variant="destructive">{tr('lab', 'not_marked')}</Badge>),
        },
        { header: tr('lab', 'reason'), sort: (row) => row.reason, cell: (row) => <span className="text-xs">{row.reason ?? '—'}</span> },
        { header: tr('lab', 'placed_by'), sort: (row) => row.orderedByName, cell: (row) => row.orderedByName ?? '—' },
      ]}
    />
  );
}
